export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-600">Last Updated: October 13, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-gray max-w-none">

          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              This is a free, personal project to help people find jobs with transparent compensation. This privacy policy explains what data I collect, how I use it, and your rights. Written in Plain English - no confusing legal jargon.
            </p>
          </section>

          {/* What Data We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What Data We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              I collect minimal data, only what's necessary to provide and improve the service:
            </p>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Anonymous Website Analytics</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Page views and navigation patterns</li>
                  <li>No personal identifiers or tracking cookies</li>
                  <li>Collected via Plausible Analytics and Vercel Analytics (both privacy-friendly)</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Waitlist Emails (Optional)</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Email address if you sign up for updates</li>
                  <li>Stored in Airtable</li>
                  <li>Only used to notify you about product updates</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Contact Form Submissions (Optional)</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Your name, email, and message when you contact me</li>
                  <li>Stored in Plain (customer support platform)</li>
                  <li>Only used to respond to your inquiry</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Newsletter Subscriptions (Optional)</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Email address if you subscribe to the newsletter</li>
                  <li>Handled directly by Substack (not stored by me)</li>
                  <li>Governed by Substack's privacy policy</li>
                </ul>
              </div>

            </div>

            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">What I DON'T Collect:</h3>
              <ul className="list-disc list-inside text-green-800 space-y-1">
                <li>No tracking cookies</li>
                <li>No user accounts or passwords</li>
                <li>No social media tracking pixels</li>
                <li>No advertising data</li>
                <li>No behavioral profiling</li>
              </ul>
            </div>
          </section>

          {/* How We Use Your Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Data</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p><strong>Anonymous Analytics:</strong> To understand which features are useful and improve the site.</p>
              <p><strong>Waitlist Emails:</strong> To notify you when new features launch or important updates happen.</p>
              <p><strong>Contact Form:</strong> To respond to your questions, feedback, or support requests.</p>
              <p><strong>Newsletter:</strong> To send updates about jobs and the project (managed by Substack).</p>
              <p className="mt-4"><strong>I will never</strong> sell your data, use it for advertising, or send spam.</p>
            </div>
          </section>

          {/* Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              I use these trusted services to run the site. Each has their own privacy policy:
            </p>

            <div className="space-y-3">
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-semibold text-gray-900">Plausible Analytics</p>
                <p className="text-sm text-gray-600">Privacy-friendly website analytics (no cookies, GDPR compliant)</p>
                <a href="https://plausible.io/data-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                  View Plausible Data Policy →
                </a>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-semibold text-gray-900">Vercel</p>
                <p className="text-sm text-gray-600">Hosting and anonymous analytics</p>
                <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                  View Vercel Privacy Policy →
                </a>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-semibold text-gray-900">Airtable</p>
                <p className="text-sm text-gray-600">Waitlist email storage</p>
                <a href="https://www.airtable.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                  View Airtable Privacy Policy →
                </a>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-semibold text-gray-900">Plain</p>
                <p className="text-sm text-gray-600">Contact form and customer support</p>
                <a href="https://www.plain.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                  View Plain Privacy Policy →
                </a>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-semibold text-gray-900">Substack</p>
                <p className="text-sm text-gray-600">Newsletter subscriptions</p>
                <a href="https://substack.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                  View Substack Privacy Policy →
                </a>
              </div>

            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              I believe in respecting your privacy. You have the right to:
            </p>

            <div className="space-y-3">
              <div className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <div>
                  <p className="font-semibold text-gray-900">Access Your Data</p>
                  <p className="text-gray-600 text-sm">Request a copy of any data I have about you</p>
                </div>
              </div>

              <div className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <div>
                  <p className="font-semibold text-gray-900">Delete Your Data</p>
                  <p className="text-gray-600 text-sm">Request deletion of your waitlist email or contact form messages</p>
                </div>
              </div>

              <div className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <div>
                  <p className="font-semibold text-gray-900">Opt-Out Anytime</p>
                  <p className="text-gray-600 text-sm">Unsubscribe from emails or stop using the service</p>
                </div>
              </div>
            </div>

            <p className="text-gray-700 mt-6">
              To exercise these rights, use the contact form (click the message icon in the bottom-right corner) and I'll respond as soon as possible.
            </p>
          </section>

          {/* Job Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">About Job Data</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              The job listings on this site come from public APIs provided by companies' Applicant Tracking Systems
              (Lever, Greenhouse, Ashby). This data is:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Already publicly available</li>
              <li>Analyzed using AI to extract compensation and other details</li>
              <li>Displayed for free to help job seekers</li>
              <li>May be outdated or inaccurate (not guaranteed)</li>
            </ul>
            <p className="text-gray-700 mt-4">
              When you apply for a job, you'll be redirected to the company's official application page.
              I don't handle applications or collect any application data.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              I may update this privacy policy from time to time. When I do, I'll update the "Last Updated" date at the top.
              For significant changes, I'll post a notice on the site or notify you via email if you're on the waitlist.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Me</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Questions about this privacy policy or how I handle your data? I'm happy to help.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-900 mb-2">
                Use the contact form by clicking the message icon in the bottom-right corner of any page.
              </p>
              <p className="text-sm text-gray-600">
                This is a personal project operated by an individual, not a registered business.
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  )
}
